/**
 * Inventory Indicator
 * Compact pill/badge showing inventory capacity with color coding.
 * Triggers a one-time toast warning at 80% capacity.
 */

import { useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useGameStore } from '@/state';

export function InventoryIndicator() {
  const currentCount = useGameStore((s) => s.playerState.inventory.currentCount);
  const capacity = useGameStore((s) => s.playerState.inventory.capacity);
  const warningShownRef = useRef(false);

  const ratio = capacity > 0 ? currentCount / capacity : 0;
  const isFull = currentCount >= capacity;

  // One-time toast at 80% capacity (side effect in useEffect, not render)
  useEffect(() => {
    if (ratio >= 0.8 && !isFull && !warningShownRef.current) {
      warningShownRef.current = true;
      toast(`Inventory getting full! (${currentCount}/${capacity})`, {
        icon: '📦',
        duration: 3000,
      });
    }

    // Reset warning flag when inventory drops below 70%
    if (ratio < 0.7) {
      warningShownRef.current = false;
    }
  }, [ratio, isFull, currentCount, capacity]);

  const colorClass = isFull
    ? 'text-red-400 border-red-500/50 bg-red-900/30'
    : ratio >= 0.9
    ? 'text-orange-400 border-orange-500/40 bg-orange-900/20'
    : ratio >= 0.7
    ? 'text-amber-400 border-amber-500/30 bg-amber-900/20'
    : 'text-gray-400 border-gray-600 bg-gray-800/40';

  const pulseClass = isFull ? 'animate-pulse' : '';

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${colorClass} ${pulseClass}`}
    >
      <span>📦</span>
      <span>{currentCount} / {capacity}</span>
    </div>
  );
}
